import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Users,
  Shield,
  Send,
  Handshake,
  BarChart3,
  MessageSquare,
  Gift,
  Check,
  Menu,
  X,
} from 'lucide-react';
import './ClubLandingPage.css';
import ClubRegistrationForm from './ClubRegistrationForm';

// Assets
import darkLogo from '../../Assets/Images/Dark_Logo_Transparent.png';

// SEO structured data
const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PrivateIQ',
  url: 'https://privateiq.golf/',
  logo: 'https://privateiq.golf/logo192.png',
};

// Feature card component
const FeatureCard = ({ icon, title, description }) => (
  <div className="cl-feature-card">
    <div className="cl-feature-icon">{icon}</div>
    <h3 className="cl-feature-title">{title}</h3>
    <p className="cl-feature-description">{description}</p>
  </div>
);

// Process step component
const ProcessStep = ({ number, icon, title, description, note }) => (
  <div className="cl-process-step">
    <div className="cl-process-number">{number}</div>
    <div className="cl-process-content">
      <div className="cl-process-header">
        <div className="cl-process-icon">{icon}</div>
        <h3 className="cl-process-title">{title}</h3>
      </div>
      <p className="cl-process-description">{description}</p>
      <div className="cl-process-note">
        <p>{note}</p>
      </div>
    </div>
  </div>
);

// Pricing feature check
const PricingFeature = ({ text }) => (
  <div className="cl-pricing-feature">
    <Check className="cl-pricing-check" strokeWidth={3} />
    <span>{text}</span>
  </div>
);

const ClubLandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll handler for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  // SEO
  const seoTitle = 'PrivateIQ for Clubs — Reach Qualified Golf Prospects';
  const seoDescription =
    'Access anonymous profiles of golfers actively exploring private club membership. Connect with qualified prospects through IQ Edge — the intelligent way for clubs to discover new members.';

  return (
    <div className="club-landing-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://privateiq.golf/myclub/register" />
        <meta name="theme-color" content="#2c5c4f" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PrivateIQ" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content="https://privateiq.golf/myclub/register" />
        <meta property="og:image" content="https://privateiq.golf/og-cover.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      {/* ===== HEADER ===== */}
      <header className={`cl-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="cl-header-inner">
          {/* Logo */}
          <Link to="/" className="cl-header-logo">
            <img src={darkLogo} alt="PrivateIQ" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="cl-header-nav">
            <Link to="/">For Golfers</Link>
            <button onClick={() => scrollToSection('how-it-works')}>How It Works</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
          </nav>

          {/* Auth Buttons (Desktop) */}
          <div className="cl-header-auth">
            <div className="cl-header-divider"></div>
            <Link to="/myclub/login" className="cl-header-login">Club Login</Link>
            <button
              className="cl-header-register"
              onClick={() => scrollToSection('register')}
            >
              Get Started
            </button>
          </div>

          {/* Mobile */}
          <div className="cl-header-mobile">
            <button
              className="cl-mobile-register"
              onClick={() => scrollToSection('register')}
            >
              Get Started
            </button>
            <button
              className="cl-header-mobile-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="cl-w-5 cl-h-5" /> : <Menu className="cl-w-5 cl-h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="cl-mobile-menu">
            <nav className="cl-mobile-nav">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>For Golfers</Link>
              <button onClick={() => scrollToSection('how-it-works')}>How It Works</button>
              <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            </nav>
            <div className="cl-mobile-auth">
              <Link to="/myclub/login" onClick={() => setMobileMenuOpen(false)}>Club Login</Link>
              <button
                className="cl-mobile-register-btn"
                onClick={() => scrollToSection('register')}
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ===== HERO SECTION ===== */}
      <section id="register" className="cl-hero">
        <div className="cl-hero-bg"></div>
        <div className="cl-section-container">
          <div className="cl-hero-grid">
            {/* Left: Headline & Description */}
            <div className="cl-hero-content">
              <div className="cl-hero-badge">For Private Clubs</div>
              <h1 className="cl-hero-headline">
                Reach <span>Qualified Prospects</span> Before Your Competition
              </h1>
              <p className="cl-hero-description">
                Access anonymous profiles of golfers actively exploring private club membership.
                Connect with the right prospects on their terms through our privacy-first platform.
              </p>
            </div>

            {/* Right: Registration Form */}
            <div className="cl-hero-form">
              <ClubRegistrationForm />
            </div>
          </div>
        </div>
      </section>

      {/* ===== PLATFORM DEMO ===== */}
      <section className="cl-demo">
        <div className="cl-section-container">
          <div className="cl-section-header">
            <h2 className="cl-section-title">See the Platform in Action</h2>
            <p className="cl-section-subtitle">
              A quick look at how PrivateIQ works for clubs and golfers.
            </p>
          </div>
          <div className="cl-demo-video">
            <iframe
              src="https://www.youtube.com/embed/FUhCvJ_7vFk"
              title="PrivateIQ Platform Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* ===== VALUE PROPOSITION ===== */}
      <section className="cl-features">
        <div className="cl-section-container">
          <div className="cl-section-header">
            <h2 className="cl-section-title">Why Clubs Choose PrivateIQ</h2>
            <p className="cl-section-subtitle">
              A modern approach to discovering members who are the right fit for your club.
            </p>
          </div>

          <div className="cl-features-grid">
            <FeatureCard
              icon={<Users className="cl-w-5 cl-h-5" />}
              title="Qualified Prospect Profiles"
              description="Access anonymous demographic profiles of golfers who match your club's ideal member criteria — age, income, location, and family composition."
            />
            <FeatureCard
              icon={<MessageSquare className="cl-w-5 cl-h-5" />}
              title="Direct Messaging"
              description="When a golfer opts in, communicate directly through our secure platform. Send PDFs, brochures, and invitations."
            />
            <FeatureCard
              icon={<BarChart3 className="cl-w-5 cl-h-5" />}
              title="Competitive Intelligence"
              description="Data-driven insights on nearby golfer demographics, average household income, and interest levels in your area."
            />
            <FeatureCard
              icon={<Gift className="cl-w-5 cl-h-5" />}
              title="Free to Start"
              description="Begin with 5 complimentary prospect requests. No credit card required. Upgrade when you're ready to scale."
            />
          </div>
        </div>
      </section>

      {/* ===== HOW IQ EDGE WORKS ===== */}
      <section id="how-it-works" className="cl-process">
        <div className="cl-section-container">
          <div className="cl-section-header">
            <h2 className="cl-section-title">How It Works</h2>
            <p className="cl-section-subtitle">
              A privacy-first process that connects clubs with qualified prospects.
            </p>
          </div>

          <div className="cl-process-steps">
            <ProcessStep
              number="01"
              icon={<Users className="cl-w-6 cl-h-6" />}
              title="Golfers Register"
              description="Golfers create profiles with key demographics — location, age, household income, and membership preferences."
              note="All personal contact information remains strictly confidential and is never shared without explicit approval."
            />
            <ProcessStep
              number="02"
              icon={<Shield className="cl-w-6 cl-h-6" />}
              title="Anonymous Matching"
              description="We share anonymized demographic summaries with partner clubs. No names, no emails, no identifying information."
              note="Clubs see aggregate data showing prospect density and demographic fit near their location."
            />
            <ProcessStep
              number="03"
              icon={<Send className="cl-w-6 cl-h-6" />}
              title="Express Interest"
              description="Your club expresses interest in a prospect profile. The golfer is notified and decides whether to share their contact information."
              note="100% golfer-controlled. They choose whether to engage — ensuring genuine interest."
            />
            <ProcessStep
              number="04"
              icon={<Handshake className="cl-w-6 cl-h-6" />}
              title="Connect & Welcome"
              description="When a golfer approves, contact details are shared. If you invite the golfer to tour your club, we ask that you allow them to play the course at least once at a date and time of your choosing."
              note="This builds goodwill and creates a genuine first impression with potential members."
            />
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="cl-pricing">
        <div className="cl-section-container">
          <div className="cl-section-header">
            <h2 className="cl-section-title">Simple, Transparent Pricing</h2>
            <p className="cl-section-subtitle">
              Start free and upgrade when you're ready to scale your outreach.
            </p>
          </div>

          <div className="cl-pricing-grid">
            {/* Free Tier */}
            <div className="cl-pricing-card">
              <h3 className="cl-pricing-plan">Free Tier</h3>
              <div className="cl-pricing-amount">
                <span className="cl-pricing-price">$0</span>
              </div>
              <p className="cl-pricing-note">No credit card required</p>

              <div className="cl-pricing-features">
                <PricingFeature text="5 prospect requests" />
                <PricingFeature text="Anonymous demographic profiles" />
                <PricingFeature text="Basic demographic data" />
                <PricingFeature text="Platform messaging" />
              </div>

              <button
                className="cl-pricing-cta secondary"
                onClick={() => scrollToSection('register')}
              >
                Get Started Free
              </button>
            </div>

            {/* Pro Tier */}
            <div className="cl-pricing-card recommended">
              <div className="cl-pricing-badge">Recommended</div>
              <h3 className="cl-pricing-plan">Pro Tier</h3>
              <div className="cl-pricing-amount">
                <span className="cl-pricing-price">$500</span>
                <span className="cl-pricing-period">/mo</span>
              </div>
              <p className="cl-pricing-note">Billed monthly</p>

              <div className="cl-pricing-features">
                <PricingFeature text="Unlimited prospect requests" />
                <PricingFeature text="Advanced filtering" />
                <PricingFeature text="Priority support" />
                <PricingFeature text="Platform messaging & PDF sharing" />
              </div>

              <button
                className="cl-pricing-cta"
                onClick={() => scrollToSection('register')}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="cl-final-cta">
        <div className="cl-section-container">
          <h2 className="cl-final-cta-title">Ready to Meet Your Next Members?</h2>
          <p className="cl-final-cta-subtitle">
            Join forward-thinking clubs using PrivateIQ to connect with qualified prospects
            in a way that respects golfer privacy and builds genuine relationships.
          </p>
          <button className="cl-final-cta-btn" onClick={() => scrollToSection('register')}>
            Register Your Club
          </button>
          <p className="cl-final-cta-note">Free tier available — no credit card required</p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="cl-footer">
        <div className="cl-section-container">
          <div className="cl-footer-grid">
            <div className="cl-footer-brand">
              <div className="cl-footer-logo">privateIQ</div>
              <p className="cl-footer-tagline">
                The intelligent way for clubs to discover qualified members.
              </p>
            </div>
            <div className="cl-footer-links">
              <h4>Platform</h4>
              <ul>
                <li><Link to="/">For Golfers</Link></li>
                <li><button onClick={() => scrollToSection('how-it-works')}>How It Works</button></li>
                <li><button onClick={() => scrollToSection('pricing')}>Pricing</button></li>
              </ul>
            </div>
            <div className="cl-footer-links">
              <h4>Company</h4>
              <ul>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            <div className="cl-footer-links">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/termsofservice">Terms of Service</Link></li>
                <li><Link to="/privacypolicy">Privacy Policy</Link></li>
                <li><Link to="/privacy-choices">Privacy Choices</Link></li>
              </ul>
            </div>
          </div>
          <div className="cl-footer-bottom">
            <p>&copy; 2025 Non-Target LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClubLandingPage;
